import React, { useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import { currentRpeSportDayIso } from "./rpeOverviewUtils.js";
import "react-datepicker/dist/react-datepicker.css";

function shiftIsoDate(isoDate, deltaDays) {
  const d = new Date(`${isoDate}T12:00:00`);
  d.setDate(d.getDate() + deltaDays);
  return d.toISOString().slice(0, 10);
}

function formatNavDate(isoDate, locale = "fr-FR") {
  const d = new Date(`${isoDate}T12:00:00`);
  return d.toLocaleDateString(locale, { weekday: "short", day: "2-digit", month: "short" });
}

function isoToDate(isoDate) {
  if (!isoDate) return null;
  return new Date(`${isoDate}T12:00:00`);
}

function dateToIso(date) {
  if (!date) return null;
  return date.toISOString().slice(0, 10);
}

/**
 * Date navigator with prev/next arrows and inline calendar popup.
 * Used for RPE sport days (08:00 Paris) and subjective/Hooper calendar days.
 */
const RpeSportDayNavigator = ({
  value,
  onChange,
  isDarkMode = false,
  className = "",
  todayValue = null,
  maxDate = null,
  openCalendarLabelKey = "feedback.open_calendar",
}) => {
  const { t, i18n } = useTranslation();
  const [calendarOpen, setCalendarOpen] = useState(false);
  const containerRef = useRef(null);
  const resolvedToday = todayValue || currentRpeSportDayIso();
  const resolvedMax = maxDate || resolvedToday;
  const isToday = value === resolvedToday;
  const dateLocale = i18n.language?.startsWith("en") ? "en-GB" : "fr-FR";

  useEffect(() => {
    if (!calendarOpen) return undefined;

    const handlePointerDown = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setCalendarOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [calendarOpen]);

  const handleDatePick = (date) => {
    const next = dateToIso(date);
    if (!next) return;
    onChange?.(next);
    setCalendarOpen(false);
  };

  return (
    <div
      ref={containerRef}
      className={`relative flex items-center gap-1 rounded-lg border px-2 py-1 ${
        isDarkMode ? "border-gray-600 bg-gray-800" : "border-gray-300 bg-white"
      } ${className}`}
    >
      <button
        type="button"
        onClick={() => onChange?.(shiftIsoDate(value, -1))}
        className={`p-1 rounded hover:bg-opacity-20 ${
          isDarkMode ? "hover:bg-white" : "hover:bg-black hover:bg-opacity-10"
        }`}
        title={t("common.previous_day")}
      >
        <ChevronLeft className="h-4 w-4" />
      </button>

      <button
        type="button"
        onClick={() => setCalendarOpen((open) => !open)}
        className={`inline-flex min-w-[110px] items-center justify-center gap-1.5 rounded px-1 py-0.5 text-sm font-medium transition hover:bg-opacity-10 ${
          isToday ? "text-amber-500 font-semibold" : ""
        } ${isDarkMode ? "hover:bg-white/10" : "hover:bg-black/5"}`}
        title={t(openCalendarLabelKey)}
      >
        <CalendarDays className="h-3.5 w-3.5 shrink-0 opacity-70" />
        <span>{isToday ? t("common.today") : formatNavDate(value, dateLocale)}</span>
      </button>

      <button
        type="button"
        onClick={() => onChange?.(shiftIsoDate(value, 1))}
        disabled={isToday}
        className={`p-1 rounded hover:bg-opacity-20 disabled:opacity-30 ${
          isDarkMode ? "hover:bg-white" : "hover:bg-black hover:bg-opacity-10"
        }`}
        title={t("common.next_day")}
      >
        <ChevronRight className="h-4 w-4" />
      </button>

      {calendarOpen && (
        <div
          className={`absolute right-0 top-full z-50 mt-2 rounded-xl border p-2 shadow-xl ${
            isDarkMode ? "border-gray-600 bg-gray-900" : "border-gray-200 bg-white"
          }`}
        >
          <DatePicker
            inline
            selected={isoToDate(value)}
            onChange={handleDatePick}
            maxDate={isoToDate(resolvedMax)}
            calendarStartDay={1}
          />
        </div>
      )}
    </div>
  );
};

export default RpeSportDayNavigator;
